import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Flag } from 'lucide-react';
import type { CareerStage, CareerStageRequest } from '@/types/api.types';

interface CareerStageFormProps {
  initialData?: CareerStage;
  onSubmit: (payload: CareerStageRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
  historyAircraftTypes?: string[];
  historyOperatingUnits?: string[];
}

export default function CareerStageForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
  historyAircraftTypes = [],
  historyOperatingUnits = [],
}: CareerStageFormProps) {
  const [title, setTitle] = useState('');
  const [stageType, setStageType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [aircraftType, setAircraftType] = useState('');
  const [operatingUnit, setOperatingUnit] = useState('');
  const [isMilestone, setIsMilestone] = useState(false);
  const [description, setDescription] = useState('');
  const [dateError, setDateError] = useState('');

  const dedupedAircraftTypes = Array.from(new Set(historyAircraftTypes.filter(Boolean)));
  const dedupedOperatingUnits = Array.from(new Set(historyOperatingUnits.filter(Boolean)));

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title);
      setStageType(initialData.stageType ?? '');
      setStartDate(initialData.startDate);
      setEndDate(initialData.endDate ?? '');
      setAircraftType(initialData.aircraftType ?? '');
      setOperatingUnit(initialData.operatingUnit ?? '');
      setIsMilestone(initialData.isMilestone ?? false);
      setDescription(initialData.description ?? '');
    }
  }, [initialData]);

  const validateDates = (start: string, end: string) => {
    if (start && end && end < start) {
      setDateError('结束日期不能早于开始日期');
      return false;
    }
    setDateError('');
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (!validateDates(startDate, endDate)) return;

    const payload: CareerStageRequest = {
      title: title.trim(),
      stageType: stageType.trim() || undefined,
      startDate,
      endDate: isMilestone ? startDate : (endDate || null),
      aircraftType: aircraftType.trim() || undefined,
      operatingUnit: operatingUnit.trim() || undefined,
      isMilestone,
      description: description.trim() || undefined,
    };
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">阶段类型 *</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="如：F1副驾驶、学员、机长"
            required
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="stageType">阶段事件</Label>
          <Input
            id="stageType"
            value={stageType}
            onChange={(e) => setStageType(e.target.value)}
            placeholder="如：低能见资质获取、报务资质"
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="startDate">开始日期 *</Label>
          <Input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              validateDates(e.target.value, endDate);
            }}
            required
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="endDate">结束日期</Label>
          <Input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              validateDates(startDate, e.target.value);
            }}
            disabled={isLoading}
          />
          {dateError && (
            <p className="text-sm text-red-500">{dateError}</p>
          )}
          <p className="text-xs text-gray-400">留空表示当前阶段</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="aircraftType">运行机型</Label>
          <Input
            id="aircraftType"
            list="aircraftTypeList"
            value={aircraftType}
            onChange={(e) => setAircraftType(e.target.value)}
            placeholder="如：A320"
            disabled={isLoading}
          />
          <datalist id="aircraftTypeList">
            {dedupedAircraftTypes.map((type) => (
              <option key={type} value={type} />
            ))}
          </datalist>
        </div>

        <div className="space-y-2">
          <Label htmlFor="operatingUnit">运行单位</Label>
          <Input
            id="operatingUnit"
            list="operatingUnitList"
            value={operatingUnit}
            onChange={(e) => setOperatingUnit(e.target.value)}
            placeholder="如：航司上海"
            disabled={isLoading}
          />
          <datalist id="operatingUnitList">
            {dedupedOperatingUnits.map((unit) => (
              <option key={unit} value={unit} />
            ))}
          </datalist>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="isMilestone"
          type="checkbox"
          checked={isMilestone}
          onChange={(e) => setIsMilestone(e.target.checked)}
          disabled={isLoading}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <Label htmlFor="isMilestone" className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
          <Flag className="w-3.5 h-3.5 text-amber-500" />
          里程碑事件（单日事件，如首飞、放单、执照获取）
        </Label>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">备注</Label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="补充说明..."
          rows={3}
          disabled={isLoading}
          className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          取消
        </Button>
        <Button type="submit" disabled={isLoading || !!dateError}>
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              保存中...
            </>
          ) : initialData ? (
            '保存修改'
          ) : (
            '添加阶段'
          )}
        </Button>
      </div>
    </form>
  );
}
